import { isNativeApp } from "./detect-platform"

/**
 * Os pedidos que a página faz à casca nativa (`webview/App.js`).
 *
 * O que vale para todos: só existem DENTRO do app. No navegador as funções
 * devolvem `false` e nada acontece, então a tela precisa perguntar antes
 * (`isNativeApp()`) em vez de oferecer um botão que não faz nada.
 *
 * O widget de treino (Live Activity) tem canal próprio em `lib/native-bridge.ts`.
 */

export function postToNative(payload: Record<string, unknown>): boolean {
  if (typeof window === "undefined") return false
  const bridge = (window as any).ReactNativeWebView
  if (!bridge?.postMessage) return false
  try {
    bridge.postMessage(JSON.stringify(payload))
    return true
  } catch {
    return false
  }
}

/**
 * Pede a avaliação na loja (o aviso do próprio sistema, com as estrelas).
 *
 * Quem decide se aparece é a Apple e o Google, não nós: eles limitam quantas
 * vezes por ano e ignoram o pedido em silêncio quando passa disso. Por isso
 * NUNCA prometa o aviso na tela ("toque para avaliar") nem espere resposta para
 * seguir: escute `STORE_REVIEW_UNAVAILABLE` se precisar de um caminho B.
 *
 * E não chame no meio de uma tarefa. O lugar é depois de algo dar certo.
 */
export function requestStoreReview(): boolean {
  if (!isNativeApp()) return false
  return postToNative({ type: "REQUEST_STORE_REVIEW" })
}

/** Como está a permissão de notificação neste aparelho. */
export type SituacaoDePush = {
  /** `granted` = pode notificar. `denied` = negado. `undetermined` = ainda não perguntamos. */
  status: "granted" | "denied" | "undetermined" | string
  /** `false` = o aviso do sistema não vai mais aparecer, só os Ajustes resolvem. */
  pode_pedir: boolean
  /** Presente em `PUSH_PERMISSION_RESULT`: o token, quando o "sim" acabou de sair. */
  token?: string | null
  platform?: string
}

/**
 * Faz aparecer o aviso do sistema pedindo notificações.
 *
 * A casca NÃO pede mais isso sozinha na abertura: quem chegava pela primeira
 * vez levava o aviso antes de saber o que o app faz e negava, e no iPhone essa
 * negativa é definitiva. Então o pedido é da tela, na hora em que ela já
 * explicou para que serve.
 *
 * Chame só depois de escrever o motivo na tela, e uma vez por decisão da
 * pessoa (num toque dela, nunca ao abrir a tela). A resposta chega como
 * `PUSH_PERMISSION_RESULT`, inclusive quando é "não".
 *
 * O `REQUEST_PUSH_TOKEN` vai junto de propósito: o app instalado pode ser
 * anterior a esta versão e conhecer só ele.
 */
export function requestPushPermission(): boolean {
  if (!isNativeApp()) return false
  const pediu = postToNative({ type: "REQUEST_PUSH_PERMISSION" })
  postToNative({ type: "REQUEST_PUSH_TOKEN" })
  return pediu
}

/** Pergunta a situação sem fazer aviso nenhum aparecer. Responde `PUSH_PERMISSION_STATUS`. */
export function askPushStatus(): boolean {
  if (!isNativeApp()) return false
  return postToNative({ type: "REQUEST_PUSH_TOKEN" })
}

/**
 * Abre os Ajustes do aparelho na página do app.
 *
 * É o único caminho de quem já negou (`pode_pedir: false`): o aviso do sistema
 * não volta. Ofereça isto no lugar do botão de pedir, e escreva onde a pessoa
 * vai cair, porque ela sai do app.
 */
export function openAppSettings(): boolean {
  if (!isNativeApp()) return false
  return postToNative({ type: "OPEN_APP_SETTINGS" })
}

/** Um evento do app para a Meta, no formato que a casca repassa ao SDK. */
export type EventoDaMetaNoApp = {
  /** O nome padrão da Meta (`fb_mobile_purchase`, `fb_mobile_complete_registration`...). */
  evento: string
  /** Na compra, obrigatório junto com `moeda`: sem os dois a casca não registra. */
  valor?: number
  /** Código de três letras (`BRL`). */
  moeda?: string
  /** Até 25, com texto de até 100 caracteres: a casca corta o que passar. */
  parametros?: Record<string, string | number>
}

/**
 * Entrega um marco do funil (cadastro, pagamento, compra) ao SDK da Meta que
 * vive na casca, para a campanha de app enxergar o que acontece DENTRO da
 * página. Sem isto o SDK mede só instalação e abertura.
 *
 * Quem chama é o `rastrear` (`lib/services/tracking-service.ts`), no mesmo
 * instante do pixel, e não a tela: assim o evento do app e o do site nunca
 * contam coisas diferentes. A casca de antes desta ponte ignora o aviso.
 */
export function avisarEventoDaMeta(dados: EventoDaMetaNoApp): boolean {
  if (!isNativeApp()) return false
  return postToNative({ type: "META_APP_EVENT", ...dados })
}

/** O que a casca responde. */
export type RespostaNativa =
  | ({ type: "PUSH_PERMISSION_STATUS" } & SituacaoDePush)
  | ({ type: "PUSH_PERMISSION_RESULT" } & SituacaoDePush)
  | { type: "PUSH_TOKEN"; token: string; platform?: string }
  | { type: "STORE_REVIEW_REQUESTED" }
  | { type: "STORE_REVIEW_UNAVAILABLE" }

/**
 * Escuta uma resposta da casca. Devolve a função de parar de escutar, para
 * usar direto no `return` de um `useEffect`.
 *
 * A casca fala por `window.postMessage`, então o dado chega como texto e cada
 * tela repetia o mesmo `JSON.parse` dentro de try/catch. Mensagem de outra
 * origem (e o app carrega iframe de vídeo) não é JSON nosso e é descartada.
 */
export function onNativeMessage<T extends RespostaNativa["type"]>(
  tipo: T,
  aoReceber: (dados: Extract<RespostaNativa, { type: T }>) => void,
): () => void {
  if (typeof window === "undefined") return () => {}
  const ouvinte = (evento: MessageEvent) => {
    try {
      const dados =
        typeof evento.data === "string" ? JSON.parse(evento.data) : evento.data
      if (dados?.type === tipo) aoReceber(dados)
    } catch {
      // Não é mensagem nossa.
    }
  }
  window.addEventListener("message", ouvinte)
  return () => window.removeEventListener("message", ouvinte)
}
